import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FolderIcon,
  FolderOpenIcon,
  PlusIcon,
} from "@heroicons/react/24/outline/index.js";
import { Card } from "../../UI/Card.tsx";
import { IconButton } from "../../UI/IconButton.tsx";
import { LoadingRows } from "../../UI/Loading/LoadingRows.tsx";
import { useConfirm } from "../../UI/Feedback/useConfirm.ts";
import { useAllUserGroups } from "../../UserGroup/useAllUserGroups.ts";
import {
  CreateOnboardingLivePdfScreenPath,
  editOnboardingLivePdfPathOf,
} from "../OnboardingLivePaths.ts";
import { useOnboardingLivePdfs } from "./useOnboardingLivePdfs.ts";
import { useUserGroupsMissingOnboardingLivePdf } from "./useUserGroupsMissingOnboardingLivePdf.ts";
import {
  OnboardingLivePdfRow,
  OnboardingLivePdfTable,
} from "./OnboardingLivePdfTable.tsx";

export function OnboardingLivePdfSection() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pdfs, loading, deletePdf } = useOnboardingLivePdfs();
  const { userGroups } = useAllUserGroups();
  const userGroupsMissingPdf = useUserGroupsMissingOnboardingLivePdf();
  const askConfirmation = useConfirm();
  const isOnboardingLiveRequiredBySomeGroup = userGroups.some(
    (g) => g.onboardingLiveRequired,
  );
  const rows: OnboardingLivePdfRow[] = pdfs.map((pdf) => ({
    ...pdf,
    userGroup: userGroups.find((g) => g.id === pdf.userGroupId),
  }));
  const isTeens = (row: OnboardingLivePdfRow) =>
    !!row.userGroup?.slug.toLowerCase().includes("teens");
  const adultsRows = rows.filter((row) => !isTeens(row));
  const teensRows = rows.filter(isTeens);

  async function remove(row: OnboardingLivePdfRow) {
    const confirmed = await askConfirmation({
      title: `Delete the onboarding live PDF of ${row.userGroup?.name ?? row.userGroupId}?`,
      message:
        "Without a PDF no new onboarding lives can be created for this user group.",
      confirmLabel: "Delete",
      isDestructive: true,
    });
    if (!confirmed) return;
    await deletePdf(row.userGroupId);
  }

  const toEdit = (row: OnboardingLivePdfRow) =>
    navigate(editOnboardingLivePdfPathOf(row.userGroupId));

  return (
    <Card>
      <div className={"flex items-center justify-between gap-3"}>
        <button
          onClick={() => setOpen((v) => !v)}
          className={
            "flex items-center gap-2 text-left hover:text-slate-600 transition-colors"
          }
        >
          {open ? (
            <FolderOpenIcon className={"w-5 h-5 text-slate-500"} />
          ) : (
            <FolderIcon className={"w-5 h-5 text-slate-500"} />
          )}
          <span className={"font-bold text-xl"}>Onboarding Live PDFs</span>
          {open ? (
            <ChevronDownIcon className={"w-4 h-4 text-slate-400"} />
          ) : (
            <ChevronRightIcon className={"w-4 h-4 text-slate-400"} />
          )}
        </button>
        {userGroupsMissingPdf.length > 0 ? (
          <IconButton
            label={"New Onboarding Live PDF"}
            icon={PlusIcon}
            onClick={() => navigate(CreateOnboardingLivePdfScreenPath)}
          />
        ) : null}
      </div>

      {open && (
        <div className={"mt-4"}>
          {isOnboardingLiveRequiredBySomeGroup ? null : (
            <p className={"text-sm text-slate-500 mb-4"}>
              No user group requires onboarding lives: enable them from the User
              Groups page.
            </p>
          )}
          {loading ? (
            <LoadingRows />
          ) : (
            <>
              <div>
                <h3 className={"font-semibold text-base mb-2 text-slate-600"}>
                  Adults
                </h3>
                <OnboardingLivePdfTable
                  items={adultsRows}
                  onEdit={toEdit}
                  onDelete={remove}
                />
              </div>
              <div className={"mt-6"}>
                <h3 className={"font-semibold text-base mb-2 text-slate-600"}>
                  Teens
                </h3>
                <OnboardingLivePdfTable
                  items={teensRows}
                  onEdit={toEdit}
                  onDelete={remove}
                />
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
